import type { FunctionComponent } from 'react'
import React, { Fragment } from 'react'
import { Picture, TypographyV2 as Typography } from '@which/seatbelt'
import { dynamicDatalayerPush } from '@which/shared'

import { Link } from '../../../../../shared/components/Link'
import { focusAboveCompareTray } from '../../../../../shared/components/ReviewsCompareTray'
import styles from './ArticleListingWithImage.module.scss'

export const ArticleListingWithImage: FunctionComponent<ArticleListingWithImageProps> = ({
  title,
  categoryLabel,
  categorySlug,
  articles,
}) => {
  if (articles.length === 0) {
    return null
  }

  const viewAllLabel = `All ${categoryLabel} advice`
  const viewAllSlug = `/reviews/${categorySlug}/article/guides`

  const dataLayerPush = (label: string) =>
    dynamicDatalayerPush({
      eventCategory: 'Product Listing',
      eventAction: 'Need Help Choosing',
      eventLabel: `${categorySlug} - ${label}`,
    })

  return (
    <article className={styles.container} data-which-id="need-help-section">
      <Typography tag="h3" textStyle="sb-text-heading-small" className={styles.title}>
        {title}
      </Typography>
      <Picture
        src={
          'https://media.product.which.co.uk/prod/images/original/9fb26538bf5c-need-help-choosing.webp'
        }
        sources={[
          {
            srcset:
              'https://media.product.which.co.uk/prod/images/original/af8ee02bc52e-need-help-choosing.png',
          },
        ]}
        alt="Man asking for advice."
        width="220"
        height="165"
        className={styles.image}
        imageClassName={styles.transparent}
      />
      <ul className={styles.list}>
        {articles.map((article, index) => (
          <Fragment key={`${article.id}-${index}`}>
            <li key={article.id} className={styles.item}>
              <Link
                href={article.slug}
                includeIcon={true}
                onClick={() => dataLayerPush(article.label)}
                onFocus={focusAboveCompareTray}
                data-which-id="middlepage-link"
                data-section="Need help choosing?"
                data-index={index + 1}
              >
                {article.label}
              </Link>
            </li>
            {index + 1 === articles.length && (
              <li key="view-more-articles">
                <Link
                  href={viewAllSlug}
                  includeIcon={true}
                  onClick={() => dataLayerPush(viewAllLabel)}
                  onFocus={focusAboveCompareTray}
                >
                  {viewAllLabel}
                </Link>
              </li>
            )}
          </Fragment>
        ))}
      </ul>
    </article>
  )
}

///////// IMPLEMENTATION /////////

export type ArticleListingWithImageProps = {
  title: string
  categoryLabel: string
  categorySlug: string
  articles: {
    id: number
    label: string
    slug: string
  }[]
}
